// PostToSocial AI Avatar Tutorial Series — canonical content.
// `voiceover` = spoken-word script (HeyGen-ready). `slides` = on-screen
// caption / slide-bullet version (concise phrases for screen overlays).
// Admin-only surface: /admin/tutorial-scripts (copy + download).

export const TUTORIAL_MODULES = [
  {
    id: 'intro',
    number: '0',
    title: 'Series Welcome / Intro',
    runtime: '~0:50',
    voiceover: `Hey there, and welcome to PostToSocial — your all-in-one AI marketing assistant.

If you've ever felt buried under social posts, captions, emails, and scheduling, you're in the right place. PostToSocial helps you plan an entire month of content in minutes, publish it across every major platform, capture leads, and follow up automatically — all powered by AI.

In this series, I'll walk you through the whole platform, one piece at a time. We'll start with the basics — setting up your account and connecting your social channels — and build all the way up to full campaign automation and analytics.

By the end, you'll be running professional marketing on autopilot. So let's dive in — starting with getting your account set up.`,
    slides: [
      {
        title: 'Welcome to PostToSocial',
        bullets: [
          'Your all-in-one AI marketing assistant',
          'Plan a month of content in minutes',
          'Publish everywhere · capture leads · follow up automatically',
          'What this series covers: setup → content → automation → analytics',
        ],
      },
    ],
  },
  {
    id: 'getting-started',
    number: '1',
    title: 'Getting Started & Your Account',
    runtime: '~1:30',
    voiceover: `Let's get you up and running. The first step is creating your account.

Head to PostToSocial and sign up with your email and a password. You'll get a quick verification email — click the link inside to confirm your address, and you're in. If you ever forget your password, no worries; just use the "Forgot password" link on the login screen to reset it in seconds.

For extra security, I highly recommend turning on two-factor authentication in your settings. It adds a simple code step at login, so even if someone has your password, your account stays protected.

Once you're logged in, you'll land on your Dashboard. This is your home base. At a glance you can see your usage — how many posts and AI images you've used this month against your plan — plus a handy onboarding checklist that guides you through your first setup steps.

Take a moment to visit Settings and complete your profile. And if you want to see your plan details or upgrade later, that all lives under Subscription.

That's it — your account is ready. Next, let's connect your social media accounts so PostToSocial can publish for you.`,
    slides: [
      {
        title: 'Create your account',
        bullets: [
          'Sign up with email + password',
          'Verify via email link',
          'Forgot password? Reset in seconds',
          'Enable 2FA for extra security',
        ],
      },
      {
        title: 'Your home base',
        bullets: [
          'Dashboard = usage at a glance',
          'Onboarding checklist guides setup',
          'Complete your profile in Settings',
          'Plan & upgrades under Subscription',
        ],
      },
    ],
  },
  {
    id: 'connections',
    number: '2',
    title: 'Connecting Your Social Accounts',
    runtime: '~1:40',
    voiceover: `Before PostToSocial can publish on your behalf, you'll connect your social channels. Let's open the Connections page.

PostToSocial supports all the major platforms — Facebook Pages, Instagram, LinkedIn personal profiles and company pages, Bluesky, YouTube, TikTok, and X.

For Facebook and Instagram, you'll connect through Facebook and choose which Page or Instagram account you want to post to. Instagram also offers a direct login option if you prefer. Just click Connect next to the platform, approve the permissions, and select your account.

Here's something powerful: you can connect multiple accounts on the same platform. So if you manage several Facebook Pages or Instagram profiles, you can link them all and choose exactly where each post goes.

If a connection ever expires — which can happen when a platform refreshes its security tokens — you'll see a prompt to reconnect. Just click reconnect and re-approve; it only takes a moment.

Once your accounts are connected, you're ready for the fun part — creating content with AI. Let's go.`,
    slides: [
      {
        title: 'Connect your channels',
        bullets: [
          'Facebook · Instagram · LinkedIn (profile + company)',
          'Bluesky · YouTube · TikTok · X',
          'Click Connect → approve → pick account',
          'Instagram: connect via Facebook or Direct login',
        ],
      },
      {
        title: 'Tips',
        bullets: [
          'Connect MULTIPLE accounts per platform',
          'Choose exactly where each post goes',
          'Expired connection? One-click reconnect',
        ],
      },
    ],
  },
  {
    id: 'create-content',
    number: '3',
    title: 'Creating Content with AI',
    runtime: '~2:30',
    voiceover: `This is where PostToSocial really shines — AI content creation. There are several tools here, and each one fits a different need. Let me show you the lineup.

First, Generate. This is your quick-draft tool. Type what you want to post about, pick your platforms and tone, and the AI writes a polished caption with hashtags in seconds. Perfect for one-off posts.

Next, the star of the show — the AI Post Manager, also known as your Campaign Strategist. Instead of writing one post at a time, you give it a goal and an audience. The AI then builds a complete strategy, and generates a whole batch of platform-tuned posts — each with captions, hashtags, image prompts, and a smart posting schedule. It even remembers what it's already created so your content stays fresh and never repeats itself. And with one click, "Launch full campaign" will schedule your posts, generate the images, and even draft a matching email sequence.

Then there's Weekly Themes. This is for building a repeatable weekly rhythm — like Motivation Monday or Tip Tuesday. Set your themes once, and the AI fills your calendar week after week, generating images automatically in the background.

Need to turn an article or a web page into content? The URL Generator does exactly that — paste a link and get ready-to-post content.

And for longer-form work, the Studio tools cover AI Blogs, AI Ads, and AI Emails.

To keep your brand sounding like you, set up a Style Profile — it teaches the AI your voice so every post feels on-brand. And browse the Templates library for proven post structures you can reuse.

Whatever you create, it's always saved as a draft first — so you stay in full control. Up next, let's manage and publish those posts.`,
    slides: [
      {
        title: 'The AI content toolkit',
        bullets: [
          'Generate — quick single-post drafts',
          'AI Post Manager — full campaign strategy + batch posts',
          '“Launch full campaign” — posts + images + email in 1 click',
          'Weekly Themes — recurring weekly content, auto-images',
        ],
      },
      {
        title: 'More creation tools',
        bullets: [
          'URL Generator — link/article → posts',
          'Studio — AI Blogs, Ads, Emails',
          'Style Profiles — keep your brand voice',
          'Templates — proven, reusable structures',
          'Everything saves as a draft — you stay in control',
        ],
      },
    ],
  },
  {
    id: 'manage-publish',
    number: '4',
    title: 'Managing & Publishing Your Posts',
    runtime: '~2:00',
    voiceover: `Once you've generated content, everything lives under My Posts. Let's take a look.

You'll see three tabs. The Posts tab holds your drafts and scheduled content — ready to edit, schedule, or publish. The Sent tab shows everything that's already gone live, with a direct link to view each post on its platform, engagement stats like likes and comments, and quick options to copy the link, share, or delete. And the Sent Reviews tab tracks posts you've sent out for approval.

To schedule a post, just pick a date and time — or use bulk scheduling to spread a whole batch across the week automatically. Prefer a visual layout? The Calendar view shows your entire content plan at a glance, so you can see exactly what's going out and when.

You can upload your own images or video to any post, regenerate AI images if you want a different look, and rearrange your schedule anytime.

When a post's scheduled time arrives, PostToSocial publishes it for you automatically — no manual posting required. And anything you want to set aside can be moved to the Archive, or exported to a spreadsheet from the Export page.

That's your content engine, running on autopilot. Next, let's level up into full campaigns and lead capture.`,
    slides: [
      {
        title: 'My Posts — 3 tabs',
        bullets: [
          'Posts — drafts & scheduled',
          'Sent — live posts + engagement + view/copy/share',
          'Sent Reviews — items awaiting approval',
        ],
      },
      {
        title: 'Schedule & publish',
        bullets: [
          'Pick date/time or bulk-schedule a batch',
          'Calendar view = whole plan at a glance',
          'Upload media · regenerate AI images',
          'Auto-publishes at the scheduled time',
          'Archive or export to spreadsheet',
        ],
      },
    ],
  },
  {
    id: 'campaigns-funnels',
    number: '5',
    title: 'Campaigns, Funnels & Approvals',
    runtime: '~2:00',
    voiceover: `Individual posts are great — but real marketing happens in campaigns. Let's explore three tools that tie everything together.

First, the Campaign Manager. This lets you group related posts into a single campaign so you can plan, track, and launch them as one coordinated push — like a product launch or a seasonal promotion.

Next, the Funnel Builder. This is how you turn followers into leads. In a few clicks you can publish a hosted landing page — with a headline, an offer, and a sign-up form. When someone fills it out, they're captured as a lead and can be automatically added to an email list. Every funnel link is tracked, so you'll know exactly which posts are driving sign-ups.

And finally, Reviews and Approvals. If you work with clients or a team, you can send posts for review before they go live. Your client gets a clean approval link, they say yes or request changes, and you stay perfectly in sync — no messy email threads.

Together, these turn PostToSocial from a posting tool into a complete funnel: social post, to lead capture, to follow-up. Speaking of follow-up — let's talk email marketing next.`,
    slides: [
      {
        title: 'Tie it all together',
        bullets: [
          'Campaign Manager — group posts into one push',
          'Funnel Builder — hosted landing page + lead capture',
          'Funnel links are tracked → know what drives sign-ups',
          'New leads auto-added to an email list',
        ],
      },
      {
        title: 'Client approvals',
        bullets: [
          'Send posts for review before they go live',
          'Clients approve or request changes via a link',
          'Stay in sync — no messy email threads',
        ],
      },
    ],
  },
  {
    id: 'email-marketing',
    number: '6',
    title: 'Email Marketing',
    runtime: '~2:10',
    voiceover: `PostToSocial isn't just social — it's a full email marketing platform too. Let's open the Email section.

First, you'll connect an email provider. You can use SendGrid, or plug in your own custom SMTP server. This is what actually delivers your emails to inboxes.

Next, your audience. Subscribers are your contacts, and you organize them into Lists — like newsletter subscribers, or leads from a specific funnel. Remember those funnel sign-ups from the last video? They can flow straight into a list automatically.

Now for sending. Campaigns are your one-time broadcasts — think newsletters or announcements. You'll write them in the built-in editor, and after sending, you can track opens and clicks on the stats page.

But the real power is in Sequences. A sequence is an automated email series — a nurture drip — that sends a series of messages over days or weeks. Set it up once, and every new subscriber gets the right message at the right time, completely hands-off.

You can even connect a list so that anyone who joins is automatically enrolled into a sequence. That means a lead can come in from a social post, land on your funnel, join your list, and start receiving your nurture emails — all without you lifting a finger.

That's marketing automation. Next, let's automate your engagement, too.`,
    slides: [
      {
        title: 'Full email platform',
        bullets: [
          'Connect a provider: SendGrid or custom SMTP',
          'Subscribers → organized into Lists',
          'Funnel sign-ups flow into lists automatically',
        ],
      },
      {
        title: 'Send & automate',
        bullets: [
          'Campaigns — one-time broadcasts (track opens/clicks)',
          'Sequences — automated nurture drip',
          'Set once → right message, right time, hands-off',
          'List join → auto-enroll into a sequence',
        ],
      },
    ],
  },
  {
    id: 'engagement-automation',
    number: '7',
    title: 'Engagement Automation',
    runtime: '~1:40',
    voiceover: `Replying to every comment and message by hand doesn't scale. That's where Engagement Automation comes in.

Let's open Comment Automation. This feature watches your Instagram posts for specific keywords in the comments — and when someone comments that keyword, PostToSocial can automatically reply to them publicly and send them a direct message.

Here's a classic example: you post something and say "comment the word GUIDE and I'll send you my free checklist." When someone comments GUIDE, PostToSocial instantly DMs them the link. It's a proven way to turn engagement into leads, running around the clock.

Setting it up is simple. You create a rule, choose your trigger keyword, write your reply and your DM message, and enable the webhook connection that lets Instagram notify PostToSocial in real time. And before you go live, you can use "Test this rule" to make sure everything works exactly as expected.

This is how top creators capture leads automatically from every viral post. Next, let's look at how agencies and teams manage multiple brands.`,
    slides: [
      {
        title: 'Comment → DM on autopilot',
        bullets: [
          'Watches IG comments for a keyword',
          'Auto public reply + auto DM',
          'e.g. “Comment GUIDE → instant DM with your link”',
          'Turns engagement into leads 24/7',
        ],
      },
      {
        title: 'Set it up',
        bullets: [
          'Create a rule + trigger keyword',
          'Write the reply and the DM',
          'Enable the webhook connection',
          '“Test this rule” before going live',
        ],
      },
    ],
  },
  {
    id: 'multi-client',
    number: '8',
    title: 'Agencies & Multi-Client Management',
    runtime: '~1:50',
    voiceover: `If you manage social media for more than one brand — whether you're an agency or a busy team — PostToSocial is built for you. Let's look at multi-client management.

First, Clients. Each client is a separate brand you manage, with its own connections, content, and style. You can switch between clients and filter your entire workspace to just one brand at a time, so nothing ever gets mixed up.

Next, Organizations. This is your team layer. You can invite team members, assign them roles, and — importantly — control exactly which clients each person can access. So a contractor might only see the two clients they work on, while you, as the owner, see everything.

This client scoping keeps your workspace clean and secure as you grow. Every post, every campaign, every email can be tagged to the right client — which also makes reporting per-client a breeze.

Whether you're running one brand or fifty, the system stays organized. Now let's see how all this activity turns into insights — with analytics.`,
    slides: [
      {
        title: 'Manage many brands',
        bullets: [
          'Clients — each brand separate (connections, content, style)',
          'Switch & filter your workspace by one client',
          'Nothing ever gets mixed up',
        ],
      },
      {
        title: 'Teams & access',
        bullets: [
          'Organizations — invite members, assign roles',
          'Per-member client access (scoping)',
          'Tag posts/campaigns/emails to the right client',
          'Per-client reporting made easy',
        ],
      },
    ],
  },
  {
    id: 'analytics',
    number: '9',
    title: 'Analytics & Insights',
    runtime: '~1:20',
    voiceover: `Great marketing is measured marketing. Let's open the Analytics dashboard.

Here you get a clear view of how your content is performing — across platforms and over time. You can see your activity, your output, and how your audience is responding.

For individual posts, head to the Sent tab under My Posts. With a click, PostToSocial pulls in live engagement stats — likes, comments, and shares — for each published post, right where the supported platforms allow it. It's a simple way to spot which posts are winning so you can do more of what works.

Use these insights to refine your strategy: double down on your best topics, your best formats, and your best posting times. Next up — let's cover plans, billing, and how to scale your account.`,
    slides: [
      {
        title: 'Measure what matters',
        bullets: [
          'Analytics dashboard — performance across platforms & time',
          'Sent tab — live likes/comments/shares per post',
          'Spot your winning posts',
          'Double down on best topics, formats & times',
        ],
      },
    ],
  },
  {
    id: 'billing',
    number: '10',
    title: 'Plans, Billing & Upgrading',
    runtime: '~1:30',
    voiceover: `Let's talk about plans and billing. Open the Subscription page.

PostToSocial offers several tiers — from a free plan to get started, up through Starter, Growth, Pro, and Enterprise. Each plan gives you a monthly allowance of AI posts and AI images, with higher tiers unlocking more volume and more advanced features.

You can watch your usage anytime on the dashboard — it shows how much of your monthly allowance you've used and how much is left. Your allowance resets automatically on your billing cycle, so you always start fresh each period.

When you're ready to do more, upgrading is quick and secure through Stripe checkout. And if you ever hit your monthly limit, PostToSocial will let you know and offer a one-click path to upgrade or grab more capacity — so you're never stuck mid-campaign.

New accounts may also see a low-cost trial offer to explore the paid features risk-free. Pick the plan that matches your volume, and scale up whenever you grow. Almost done — let's cover support and getting help.`,
    slides: [
      {
        title: 'Plans that scale',
        bullets: [
          'Free → Starter → Growth → Pro → Enterprise',
          'Monthly allowance of AI posts + AI images',
          'Higher tiers = more volume & features',
          'Usage resets automatically each billing cycle',
        ],
      },
      {
        title: 'Upgrading',
        bullets: [
          'Secure Stripe checkout',
          'Hit your limit? One-click upgrade',
          'Low-cost trial may be available for new accounts',
        ],
      },
    ],
  },
  {
    id: 'support',
    number: '11',
    title: 'Support & Getting Help',
    runtime: '~1:00',
    voiceover: `Stuck on something? PostToSocial makes help easy to find.

Right inside the app, there's a help chat widget — click it anytime to ask a question and get guidance on the spot. For anything that needs a closer look, the Support page lets you reach the team directly.

You'll also find a Tutorials section to learn features at your own pace, and a What's New page — that's your changelog, where every new feature and improvement gets announced. Keep an eye on it, because the platform is always evolving.

Between in-app chat, support, and these resources, you're never on your own. Finally, let's take a look at the tools reserved for platform administrators.`,
    slides: [
      {
        title: 'Help is everywhere',
        bullets: [
          'In-app help chat widget',
          'Support page — reach the team',
          'Tutorials — learn at your own pace',
          'What’s New — every update announced',
        ],
      },
    ],
  },
  {
    id: 'admin',
    number: '12',
    title: 'Platform Admin (Admin-Only)',
    runtime: '~1:50',
    adminOnly: true,
    voiceover: `This last module is for platform administrators — the people who run PostToSocial behind the scenes. If that's you, let's open the Admin dashboard.

From here, you have full control of the platform. The Users section lets you view and manage every account — including the ability to impersonate a user to troubleshoot exactly what they see, all fully logged for security.

The Tier Editor is especially powerful. Every plan limit — monthly posts, AI images, pricing, and features — is configured right here in the dashboard. No code required. Change a number, save, and it's live.

You'll also manage core Settings: your Stripe payment keys, your OpenAI configuration, and your email delivery settings. And with Feature Flags, you can switch features on or off across the platform — perfect for gradual rollouts.

Rounding it out, you've got transaction history, deployment health checks, and Stripe sync tools to keep everything running smoothly.

In short, admins can configure, monitor, and manage the entire platform from one place — without touching the codebase.`,
    slides: [
      {
        title: 'Run the platform',
        bullets: [
          'Users — view, manage, impersonate (logged)',
          'Tier Editor — limits/pricing/features, no code',
          'Settings — Stripe, OpenAI, email delivery',
          'Feature Flags — toggle features platform-wide',
          'Transactions · health checks · Stripe sync',
        ],
      },
    ],
  },
  {
    id: 'outro',
    number: '13',
    title: 'Series Wrap-Up / Outro',
    runtime: '~0:45',
    voiceover: `And that's PostToSocial, end to end.

You've seen how to set up your account, connect every major platform, create content with AI, schedule and publish on autopilot, build funnels and campaigns, run email marketing, automate your engagement, manage multiple brands, and track what's working.

The big idea is simple: PostToSocial takes you from a single social post, all the way to a captured lead and an automated follow-up — so your marketing keeps working even when you're not.

Now it's your turn. Jump in, connect your first account, and let the AI plan your next month of content in minutes. I'll see you inside PostToSocial.`,
    slides: [
      {
        title: 'You’re ready to go',
        bullets: [
          'Setup → content → publishing → funnels → email → automation',
          'Social post → captured lead → automated follow-up',
          'Your marketing works even when you don’t',
          'Connect your first account & start today',
        ],
      },
    ],
  },
];

// Build a single Markdown document of the whole series (scripts + slides).
export function buildTutorialMarkdown() {
  const lines = [
    '# PostToSocial — AI Avatar Tutorial Series',
    '## Voiceover Scripts + On-Screen Slide Bullets',
    '',
  ];
  for (const m of TUTORIAL_MODULES) {
    lines.push(`---`, '', `## VIDEO ${m.number} — ${m.title}`, `*Est. runtime: ${m.runtime}*${m.adminOnly ? '  _(admin-only — consider keeping private)_' : ''}`, '');
    lines.push('### Voiceover script', '', m.voiceover, '');
    lines.push('### On-screen slides / bullets', '');
    for (const s of m.slides) {
      lines.push(`**${s.title}**`);
      for (const b of s.bullets) lines.push(`- ${b}`);
      lines.push('');
    }
  }
  return lines.join('\n');
}

// Plain-text version of one module (script + slides) for clipboard copy.
export function moduleToText(m) {
  const out = [`VIDEO ${m.number} — ${m.title} (${m.runtime})`, '', 'VOICEOVER SCRIPT:', m.voiceover, '', 'ON-SCREEN SLIDES / BULLETS:'];
  for (const s of m.slides) {
    out.push('', s.title);
    for (const b of s.bullets) out.push(`  • ${b}`);
  }
  return out.join('\n');
}
